// @ts-nocheck
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { validateLaya } from '../layaLlm';
import { kbId, kbName, kbReady } from './lookups';

const { API_MAX_ITEMS, USER_INSTRUCTION_MAX_ITEMS, FOLLOWUP_MAX_ITEMS } = AI_AGENT_FIELD_LIMITS;


export const GROUPS = [
  ['Knowledge and actions', [['kb', 'Knowledge base'], ['instructions', 'Instructions'], ['functions', 'Functions'], ['mcp', 'MCP servers'], ['variables', 'Data variables']]],
  ['Basics', [['persona', 'Persona'], ['voice', 'Voice'], ['languages', 'Language']]],
  ['Fast answers', [['laya', 'Fast answers']]],
  ['How calls end', [['handover', 'Human handover'], ['completion', 'Completion rules'], ['followup', 'Follow-ups']]],
  ['Review', [['flow', 'Flow chart']]],
];
export const ORDER = GROUPS.flatMap(([, items]) => items.map(([id, label]) => ({ id, label })));


export const REQUIRED_IDS = ['persona', 'voice', 'kb', 'instructions', 'followup', 'flow'];

export const isFilledRule = (r) => !!(r.type?.trim() || r.description?.trim());
export const ruleOk = (r) => !!r.type?.trim() && /^[a-z0-9_]+$/.test(r.type.trim()) && !!r.description?.trim();


export function facts(form) {
  const p = form.personaData || {};

const filled = form.completionRules.filter(isFilledRule);
  const enabledInstr = form.instructions.filter((i) => i.enabled !== false && i.body?.trim());
  const mcpHalf = form.mcpServers.some((s) => !!s.name?.trim() !== !!s.url?.trim());
  const mcpDone = form.mcpServers.some((s) => s.name?.trim() && s.url?.trim());
  return {
    personaDone: !!form.personaType && !!p.greeting?.trim() && !!p.display_name?.trim() && !!p.role_description?.trim(),
    enabledInstr,
    filledRules: filled,
    rulesOk: filled.every(ruleOk),
    mcpHalf,
    mcpDone,
    layaErrors: validateLaya(form.laya).errors,
    followUps: (form.followUp?.steps || []).filter((s) => s.message?.trim() && String(s.waitTime ?? '').trim()),
    followHalf: (form.followUp?.steps || []).some((s) => !!s.message?.trim() !== !!String(s.waitTime ?? '').trim()),
    canFlow: !!form.selectedKbId && enabledInstr.length > 0,
  };
}


export function nodeInfo(id, form, ctx) {
  const f = facts(form);
  const shown = (key) => ctx.attempted || ctx.touched[key];
  const seen = ctx.readOnly || !!ctx.touched[id];
  const idle = ['', ''];
  const ok = (label) => ['ok', label];
  const bad = ['bad', 'Fix'];
  switch (id) {
    case 'persona':
      return f.personaDone ? ok('Done') : shown('persona') ? bad : idle;
    case 'voice':
      return form.voiceId ? ok('Done') : shown('voice') ? bad : idle;
    case 'languages':
      return seen ? ok(form.language ? 'Set' : 'Default') : ['', 'Default'];
    case 'kb':
      return form.selectedKbId ? ok('Done') : shown('kb') ? bad : idle;
    case 'instructions':
      return f.enabledInstr.length ? ok(`${form.instructions.length}/${USER_INSTRUCTION_MAX_ITEMS}`) : shown('instructions') ? bad : idle;
    case 'functions':
      return seen ? ok(form.functions.length ? `${form.functions.length}/${API_MAX_ITEMS}` : 'Done') : idle;
    case 'mcp':
      return f.mcpHalf && shown('mcp') ? bad : seen && !f.mcpHalf ? ok(f.mcpDone ? String(form.mcpServers.length) : 'Done') : idle;
    case 'variables':
      return seen ? ok(form.dataVariables.length ? String(form.dataVariables.length) : 'Done') : idle;
    case 'laya':
      if (f.layaErrors.length && (ctx.attempted || ctx.touched.laya)) return bad;
      return form.laya.enabled ? ok('On') : form.laya.answers.length ? ['off', 'Paused'] : ['', 'Off'];
    case 'completion':
      if (!f.rulesOk) return shown('completion') ? bad : idle;
      return f.filledRules.length ? ok('Done') : ['', 'Optional'];
    case 'followup':
       if (f.followHalf) return shown('followup') ? bad : idle;
      if (f.followUps.length) return ok(`${f.followUps.length}/${FOLLOWUP_MAX_ITEMS}`);
      return shown('followup') ? bad : idle;
    case 'handover':
      return seen ? ok(form.humanHandoverEnabled ? 'On' : 'Off') : form.humanHandoverEnabled ? ['', 'On'] : ['off', 'Off'];
    case 'flow':
      return ctx.flowGenerated ? ok('Done') : f.canFlow ? idle : ['', 'Locked'];
    default:
      return idle;
  }
}


export const NEXT_TEXT = {
  persona: 'Finish the persona',
  voice: 'Pick a voice',
  kb: 'Choose a knowledge base',
  instructions: 'Turn on an instruction',
  completion: 'Add a completion rule',
  followup: 'Add a follow-up',
  flow: 'Generate the flow chart',
  mcp: 'Fix the MCP server',
  laya: 'Fix a fast answer',
};


export function readiness(form, missing, ctx, data) {
  const f = facts(form);
  const missingIds = new Set(missing.map((m) => m.id));
  const p = form.personaData || {};
  const persona = data.personas.items.find((x) => String(x.id) === String(form.personaType));
  const voice = data.voices.items.find((x) => String(x.id) === String(form.voiceId));
  const kb = data.kbs.items.find((x) => String(kbId(x)) === String(form.selectedKbId));

  const lack = [
    !form.personaType && 'a persona type',
    !p.display_name?.trim() && 'a name',
    !p.role_description?.trim() && 'a role',
    !p.greeting?.trim() && 'a greeting',
  ].filter(Boolean);

  const items = [
    {
      id: 'persona',
      label: 'Persona',
      done: !missingIds.has('persona'),
      sub: !missingIds.has('persona')
        ? [p.display_name, persona?.name].filter(Boolean).join(', ')
        : `Add ${lack.join(', ').replace(/, ([^,]*)$/, ' and $1')}`,
    },
    {
      id: 'voice',
      label: 'Voice',
      done: !missingIds.has('voice'),
      sub: voice ? voice.name : 'Pick a voice so callers hear someone',
    },
    {
      id: 'kb',
      label: 'Knowledge base',
      done: !missingIds.has('kb'),
      sub: kb ? kbName(kb) : form.selectedKbId ? 'Attached' : 'Attach one to answer from',
    },
    {
      id: 'instructions',
      label: 'Instructions',
      done: !missingIds.has('instructions'),
      sub: f.enabledInstr.length ? `${f.enabledInstr.length} on` : 'Turn on at least one',
    },
     {
      id: 'followup',
      label: 'Follow-ups',
      done: !missingIds.has('followup'),
      sub: f.followHalf ? 'Add both a wait time and a message, or remove the row' : f.followUps.length ? `${f.followUps.length} set` : 'Add at least one follow-up',
    },
    {
      id: 'flow',
      label: 'Flow chart',
      done: !!ctx.flowGenerated,
      locked: !ctx.flowGenerated && !f.canFlow,
      sub: ctx.flowGenerated ? 'Generated' : f.canFlow ? 'Ready to generate' : 'Needs a knowledge base and an instruction',
    },
  ];

  if (missingIds.has('completion')) items.push({ id: 'completion', label: 'Completion rules', done: false, extra: true, sub: 'Fix the rule details' });
 
  if (missingIds.has('mcp')) items.push({ id: 'mcp', label: 'MCP servers', done: false, extra: true, sub: 'A server needs a name and a URL' });
  if (form.laya?.enabled) {
    const lDone = !missingIds.has('laya');
    items.push({ id: 'laya', label: 'Fast answers', done: lDone, sub: lDone ? 'On' : f.layaErrors[0] || 'Add at least one answer' });
  } else if (missingIds.has('laya')) items.push({ id: 'laya', label: 'Fast answers', done: false, extra: true, sub: f.layaErrors[0] || 'An answer needs attention' });
  return items;
}


export function heroInfo(form, items, data) {
  const req = items.filter((x) => !x.extra);
  const done = req.filter((x) => x.done).length;
  const left = items.filter((x) => !x.done).length;
  const persona = data.personas.items.find((x) => String(x.id) === String(form.personaType));
  const voice = data.voices.items.find((x) => String(x.id) === String(form.voiceId));
  return {
    name: form.personaData?.display_name?.trim() || '',
    role: form.personaData?.role_description?.trim() || '',
    persona: persona?.name || '',
    voice: voice?.name || '',
    voiceSampleUrl: voice?.sample_url || '',
    language: form.language || 'en-IN',
    done,
    total: req.length,
    left,
  };
}

export { kbReady };
